import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  CashierState,
  clientAppInterceptor,
  ENV_CONFIG,
  I18nState,
  localeInterceptor,
  noCacheInterceptor,
  OnlineBookingState,
  OPERATION_DETAIL_OPENER
} from '@beautyapp/core';
import { OperationDetailDrawerOpener } from '@beautyapp/operation';
import { LayoutModule } from '@beautyapp/layout';
import { environment } from '../environments/environment';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideStore } from '@ngxs/store';
import {
  AuthState,
  branchInterceptor,
  deviceInterceptor,
  httpErrorInterceptor,
  tokenInterceptor
} from '@beautyapp/auth';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { StorageOption, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { NavigationActionTiming, withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { provideTranslateService, TranslateLoader, Translation } from '@ngx-translate/core';
import { provideHttpClient, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import ka from '@angular/common/locales/ka';
import en from '@angular/common/locales/en';
import ru from '@angular/common/locales/ru';
import kaTranslations from '../../public/assets/i18n/ka.json';
import enTranslations from '../../public/assets/i18n/en.json';
import ruTranslations from '../../public/assets/i18n/ru.json';
import { NgxSpinnerModule } from 'ngx-spinner';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideServiceWorker } from '@angular/service-worker';
import { FormsModule } from '@angular/forms';
import { en_US, ka_GE, NZ_DATE_CONFIG, NZ_I18N, ru_RU } from 'ng-zorro-antd/i18n';
import { provideClarity } from 'ngx-clarity';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { AppointmentState } from '@beautyapp/appointment';

registerLocaleData(ka);
registerLocaleData(en);
registerLocaleData(ru);

// Inline translation bundles so they are available at module-evaluation time
// instead of being fetched from /assets/i18n/*.json after bootstrap. The HTTP
// loader race was producing visible raw keys (PAYMENTTYPE.1, statistic.* etc.)
// on hard reload right after a release, before the bundle finished loading.
const STATIC_TRANSLATIONS: Record<string, Translation> = {
  ka: kaTranslations as Translation,
  en: enTranslations as Translation,
  ru: ruTranslations as Translation,
};

class StaticTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<Translation> {
    return of(STATIC_TRANSLATIONS[lang] ?? STATIC_TRANSLATIONS['ka']);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideStore([AuthState, I18nState, CashierState, AppointmentState, OnlineBookingState],
      withNgxsFormPlugin(),
      withNgxsRouterPlugin({
        navigationActionTiming: NavigationActionTiming.PostActivation
      }),
      withNgxsStoragePlugin({
        keys: [
          'auth.token',
          'auth.user',
          'auth.roles',
          'auth.permissions',
          'auth.activeBranch',
          'auth.isAuthenticated',
          'auth.notificationCount',
          'i18n.currentLang',
          'appointment.calendarPosition',
          'appointment.calendarInterval'
        ],
        storage: StorageOption.SessionStorage
      }),
      withNgxsReduxDevtoolsPlugin()
    ),
    provideHttpClient(
      withInterceptors([
        deviceInterceptor,
        tokenInterceptor,
        branchInterceptor,
        httpErrorInterceptor,
        noCacheInterceptor,
        localeInterceptor,
        clientAppInterceptor
      ]),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-CSRF-TOKEN'
      })
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: StaticTranslateLoader
      }
    }),
    importProvidersFrom([
      FormsModule,
      LayoutModule,
      NgxPermissionsModule.forRoot(),
      NgxSpinnerModule,
      NzModalModule,
      NzDrawerModule
    ]),
    provideAnimationsAsync(),
    provideFirebaseApp(() =>
      initializeApp(environment.firebaseConfig)
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideClarity({
      enabled: environment.production,
      projectId: 'md3k3tar7d'
    }),
    {
      provide: ENV_CONFIG,
      useValue: environment
    },
    {
      provide: NZ_I18N, useFactory: (localId: string) => {
        switch (localId) {
          case 'ka_GE':
            return ka_GE;
          case 'en_US':
            return en_US;
          /** keep the same with angular.json/i18n/locales configuration **/
          case 'ru_RU':
            return ru_RU;
          default:
            return ka_GE;
        }
      },
      deps: [LOCALE_ID]
    },
    {
      provide: LOCALE_ID,
      useFactory: () => {
        // NGXS stores i18n.currentLang in sessionStorage as JSON string
        const storedLang = sessionStorage.getItem('i18n.currentLang');
        const lang = storedLang ? JSON.parse(storedLang) : 'ka';
        // Map short lang codes to locale IDs
        const localeMap: Record<string, string> = { ka: 'ka_GE', en: 'en_US', ru: 'ru_RU' };
        return localeMap[lang] || 'ka_GE';
      }
    },
    {
      provide: NZ_DATE_CONFIG,
      useValue: {
        firstDayOfWeek: 1 // week starts on Monday (Sunday is 0)
      }
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false
      })
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    { provide: OPERATION_DETAIL_OPENER, useClass: OperationDetailDrawerOpener }
  ]
};
